import axios, { AxiosError } from 'axios';
import { createMutation, createQuery } from 'react-query-kit';

import { queryClient } from '@/services/query-client';
import { restUrl } from '@/services/utils';

import { GroupMember, SaveUserGroupPayload, UserGroup } from './user-groups.types';

const USER_GROUPS_KEY = ['userGroups'];

export interface UserGroupErrorBody {
    key?: string;
    data?: string | number | Array<string | number>;
}

const invalidateUserGroups = () => {
    queryClient.invalidateQueries({ queryKey: USER_GROUPS_KEY });
};

const useUserGroups = createQuery<UserGroup[]>({
    queryKey: USER_GROUPS_KEY,
    fetcher: async () => {
        const response = await axios.get<UserGroup[]>(restUrl('userGroups'));
        return response.data ?? [];
    },
});

const useOrganizationUsers = createQuery<GroupMember[]>({
    queryKey: ['users'],
    fetcher: async () => {
        const response = await axios.get<GroupMember[]>(restUrl('users'));
        return response.data ?? [];
    },
});

const useRoleNames = createQuery<string[]>({
    queryKey: ['rolenames', 'non-system'],
    fetcher: async () => {
        const response = await axios.get<string[]>(restUrl('rolenames'), {
            params: { type: 'non-system' },
        });
        return response.data ?? [];
    },
});

const useCreateUserGroup = createMutation<void, SaveUserGroupPayload, AxiosError<UserGroupErrorBody>>({
    mutationFn: async (group) => {
        await axios.post(restUrl('userGroups'), group);
    },
    onSuccess: invalidateUserGroups,
});

const useUpdateUserGroup = createMutation<void, SaveUserGroupPayload, AxiosError<UserGroupErrorBody>>({
    mutationFn: async (group) => {
        await axios.put(restUrl('userGroups'), group);
    },
    onSuccess: invalidateUserGroups,
});

const useDeleteUserGroup = createMutation<void, number | string, AxiosError>({
    mutationFn: async (id) => {
        await axios.delete(`${restUrl('userGroups')}/${id}`);
    },
    onSuccess: invalidateUserGroups,
});

export {
    useCreateUserGroup,
    useDeleteUserGroup,
    useOrganizationUsers,
    useRoleNames,
    useUpdateUserGroup,
    useUserGroups,
};
